/**
 * Helpers for the OBJECT_TEMPLATE_ATTRIBUTE `allowed_values` form field.
 *
 * The `ALLOWED_VALUES` datatype (backend: ObjectTemplateAttributeDataType.ALLOWED_VALUES)
 * renders a custom-object attribute as a dropdown. Its `allowed_values` column stores a
 * non-empty JSON list of options, each an object `{ const, label? }` — `const` is the value
 * persisted on the custom object, `label` (optional) is the display text. The backend
 * (`_validate_allowed_values` in resources/object_template_attribute.py) enforces: a
 * non-empty list, each item an object carrying a `const`, unique `const`s, and that any
 * `default_value` is one of the `const`s.
 *
 * In the UI the list is edited through a compact **form-array** editor
 * (`AllowedValuesEditor` — a "+ add allowed value" button plus a removable list of rows,
 * each row a value + label text input) rather than raw YAML/JSON. So the form field value
 * is the structured list of `{ const, label }` rows: these helpers normalise the stored
 * value into editor rows, clean the rows back into the stored shape on submit, and validate
 * the shape early to give feedback before the backend rejects it.
 *
 * Kept in a dedicated, dependency-light module (no JSX) so it can be unit tested without
 * importing the JSX-heavy resource config.
 */

export const ALLOWED_VALUES_DATATYPE = "ALLOWED_VALUES";

export const isAllowedValuesDatatype = (datatype?: string | null): boolean =>
    datatype === ALLOWED_VALUES_DATATYPE;

/** A single editable allowed-values row. `const` is the stored value, `label` the display text. */
export interface AllowedValueRow {
    const: string;
    label: string;
}

const asDisplayString = (value: unknown): string => {
    if (value == null) return "";
    if (typeof value === "string") return value;
    return String(value);
};

const isRowFieldBlank = (value: string): boolean => value.trim() === "";

/** True when a row carries neither a value nor a label (a freshly-added, untouched row). */
const isBlankRow = (row: AllowedValueRow): boolean =>
    isRowFieldBlank(row.const) && isRowFieldBlank(row.label);

/**
 * Stored SchemalessData -> editor rows.
 * `null` / non-array maps to an empty list (the editor then shows only the "+ add" button).
 * Each stored `{ const, label? }` object becomes a `{ const, label }` row; a bare scalar
 * (defensive: not the documented shape) is treated as its own value with an empty label.
 */
export const normalizeAllowedValuesRows = (value: unknown): AllowedValueRow[] => {
    if (!Array.isArray(value)) return [];
    return value.map((item) => {
        if (item != null && typeof item === "object" && !Array.isArray(item)) {
            const record = item as Record<string, unknown>;
            return { const: asDisplayString(record.const), label: asDisplayString(record.label) };
        }
        return { const: asDisplayString(item), label: "" };
    });
};

/**
 * Editor rows -> stored SchemalessData.
 * Drops fully-empty rows, trims values, and omits a blank `label` (so the dropdown falls back
 * to the value). An empty result maps to `null` (no allowed_values), matching the backend
 * contract for non-ALLOWED_VALUES attributes.
 */
export const allowedValuesRowsToStored = (value: unknown): unknown => {
    if (!Array.isArray(value)) return value == null ? null : value;
    const cleaned = (value as AllowedValueRow[])
        .map((row) => ({
            const: asDisplayString(row?.const).trim(),
            label: asDisplayString(row?.label).trim(),
        }))
        .filter((row) => !(row.const === "" && row.label === ""))
        .map((row) =>
            row.label === "" ? { const: row.const } : { const: row.const, label: row.label }
        );
    return cleaned.length === 0 ? null : cleaned;
};

/**
 * Validate the editor rows against the selected datatype.
 * - For the ALLOWED_VALUES datatype: require at least one option, each with a non-empty value,
 *   and unique values (mirrors the backend contract for early feedback).
 * - For any other datatype: allowed_values must be empty.
 */
export const validateAllowedValues = (
    value: unknown,
    datatype?: string | null
): string | undefined => {
    const rows = normalizeAllowedValuesRows(value).filter((row) => !isBlankRow(row));

    if (!isAllowedValuesDatatype(datatype)) {
        return rows.length === 0
            ? undefined
            : "Allowed values only apply to the ALLOWED_VALUES datatype. Leave this empty.";
    }

    if (rows.length === 0) {
        return "Add at least one allowed value.";
    }

    const seen = new Set<string>();
    for (const row of rows) {
        const constValue = row.const.trim();
        if (constValue === "") {
            return "Each allowed value needs a value.";
        }
        if (seen.has(constValue)) {
            return `Duplicate value: ${constValue}. Values must be unique.`;
        }
        seen.add(constValue);
    }
    return undefined;
};
